<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <!-- Hero Card -->
        <v-card class="sc-hero-card" elevation="3">
          <div class="sc-hero-header">
            <div class="sc-hero-header-content">
              <div class="sc-hero-logo">
                <v-icon color="white" size="30">mdi-file-tree-outline</v-icon>
              </div>
              <div>
                <div class="sc-hero-title">Sub-Claims</div>
                <div class="sc-hero-subtitle">
                  Break down claims into granular, verifiable sub-claims with mapped evidence
                </div>
              </div>
            </div>
          </div>
          <div class="sc-workflow-section">
            <v-row>
              <v-col cols="12" md="4">
                <div class="sc-metric-card">
                  <div
                    class="sc-metric-icon"
                    style="background: linear-gradient(135deg, #e3f2fd, #bbdefb);"
                  >
                    <v-icon color="primary">mdi-format-list-checks</v-icon>
                  </div>
                  <div class="sc-metric-label">Define</div>
                  <div class="sc-metric-desc">Create sub-claims that decompose your main claim</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="sc-metric-card">
                  <div
                    class="sc-metric-icon"
                    style="background: linear-gradient(135deg, #f3e5f5, #e1bee7);"
                  >
                    <v-icon color="purple">mdi-link-variant</v-icon>
                  </div>
                  <div class="sc-metric-label">Map Evidence</div>
                  <div class="sc-metric-desc">Link supporting evidence to each sub-claim</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="sc-metric-card">
                  <div
                    class="sc-metric-icon"
                    style="background: linear-gradient(135deg, #e8f5e9, #c8e6c9);"
                  >
                    <v-icon color="success">mdi-shield-check</v-icon>
                  </div>
                  <div class="sc-metric-label">Verify</div>
                  <div class="sc-metric-desc">Ensure complete coverage for full assurance</div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card>

        <!-- Back Navigation -->
        <v-btn
          class="sc-back-btn"
          outlined
          color="primary"
          @click="$router.push({ name: 'Claims' })"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('backToClaims') }}
        </v-btn>

        <div class="sc-content-wrapper">
          <!-- Search Bar -->
          <v-card class="sc-search-bar-container" elevation="2">
            <div class="sc-search-bar-header">
              <v-icon color="white" size="20" class="mr-2">mdi-magnify</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Search &amp; Filter</span>
            </div>
            <div class="sc-search-bar-body">
              <div class="flex flex-row items-center space-x-4">
                <v-select
                  v-model="searchField"
                  :items="searchFields"
                  item-text="text"
                  item-value="value"
                  :label="$t('search-field')"
                  hide-details
                  outlined
                  dense
                  class="mb-4 search-field"
                  style="max-width: 200px;"
                ></v-select>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('search')"
                  hide-details
                  class="mb-4 flex-grow-1"
                  outlined
                  dense
                  @keyup.enter="applySearch"
                />
                <v-btn class="bg-primary-500" @click="enableCreateDialog()">
                  <v-icon class="mr-2 text-primary-800">mdi-plus</v-icon>
                  {{ $t('subClaims.create') }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- Data Table Card -->
          <v-card class="sc-table-card" elevation="2">
            <div class="sc-table-header">
              <v-icon color="white" size="20" class="mr-2">mdi-table</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Sub-Claims</span>
            </div>

            <v-data-table
              :headers="getHeaders"
              :items="subClaims"
              :search="search"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: getItemsPerPageText,
                showCurrentPage: true,
              }"
              must-sort
              :sort-desc="true"
            >
              <template v-slot:[`item.evidencesCount`]="{ item }">
                <v-chip color="purple" outlined small>
                  {{ item.evidencesCount || 0 }} {{ $t('evidence.title') }}
                </v-chip>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip :color="statusChipColor(item.status)" outlined dark>
                  <span class="text-capitalize">{{ $t(item.status) }}</span>
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-edit"
                      v-on="on"
                      v-bind="attrs"
                      @click="handleSubClaimClicked(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('edit') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-view"
                      v-on="on"
                      v-bind="attrs"
                      @click="previewEvidence(item)"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('preview') }} {{ $t('evidence.title') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-delete"
                      v-on="on"
                      v-bind="attrs"
                      @click="enableDeleteDialog(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('delete.message') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>

          <v-dialog v-model="deleteClicked" max-width="500">
            <v-card class="sc-dialog-card">
              <v-card-title>{{ $t('delete.confirm') }}</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="deleteClicked = false">{{ $t('cancel') }}</v-btn>
                <v-btn color="red" text @click="deleteClaim">{{ $t('delete.message') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="createClicked" max-width="500">
            <v-card class="sc-dialog-card">
              <v-card-title>
                {{ $t('sac.createSubClaimTitle') }} {{ this.claim.title }}
              </v-card-title>
              <v-card-text>
                <v-form ref="createForm" v-model="createFormValid">
                  <v-text-field
                    v-model="subClaim.title"
                    :label="$t('subClaims.headers.title')"
                    outlined
                    dense
                    :rules="[rules.required, rules.duplicateName]"
                    :error-messages="
                      duplicateNameExists ? 'SubClaim with this name already exists' : ''
                    "
                    required
                    @input="checkDuplicateName"
                  ></v-text-field>
                  <v-textarea
                    v-model="subClaim.description"
                    :hint="$t('subClaims.subClaimDescriptionHint')"
                    :label="$t('subClaims.headers.description')"
                    auto-grow
                    rows="2"
                    max-rows="6"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-textarea>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="cancelCreate">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  :disabled="
                    !createFormValid ||
                      !subClaim.title ||
                      !subClaim.description ||
                      duplicateNameExists
                  "
                  color="green"
                  text
                  @click="createSubClaim()"
                >
                  {{ $t('sac.createClaim') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="editClicked" max-width="500">
            <v-card class="sc-dialog-card">
              <v-card-title> {{ $t('editHeader') }} {{ this.claim.title }} </v-card-title>
              <v-card-text>
                <v-form ref="editForm" v-model="editFormValid">
                  <v-text-field
                    v-model="editItem.title"
                    :label="$t('subClaims.headers.title')"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-text-field>
                  <v-textarea
                    v-model="editItem.description"
                    :hint="$t('subClaims.subClaimDescriptionHint')"
                    :label="$t('subClaims.headers.description')"
                    auto-grow
                    rows="2"
                    max-rows="6"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-textarea>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="handleCancelEdit()">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  :disabled="!this.editItem.title || !this.editItem.description"
                  color="green"
                  text
                  @click="editClaim()"
                >
                  {{ $t('sac.editClaim') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import subClaimsApi from '../api/subClaimsApi';
import EventBus from '@/helpers/event-bus.js';
import Loader from '@/components/helpers/Loader';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import evidenceApi from '../../evidence/api/evidenceApi';
import { debounce } from 'lodash';

export default {
  name: 'ComponentList',
  components: {
    Loader,
    Breadcrumbs,
  },
  mixins: [util],
  computed: {
    totalEvidences() {
      return this.evidencesData.length;
    },
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('subClaims.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('claims.headers.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: t('subClaims.headers.title'),
          value: 'title',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('subClaims.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('evidence.count'),
          value: 'evidencesCount',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: t('subClaims.headers.createdBy'),
          value: 'username',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('subClaims.headers.createdAt'),
          value: 'createdAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('subClaims.headers.updatedAt'),
          value: 'updatedAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('subClaims.headers.status'),
          value: 'status',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('subClaims.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('claims.headers.id'), value: 'id' },
        { text: this.$t('subClaims.headers.title'), value: 'title' },
        { text: this.$t('subClaims.headers.description'), value: 'description' },
        { text: this.$t('subClaims.headers.createdBy'), value: 'username' },
        { text: this.$t('subClaims.headers.createdAt'), value: 'createdAt' },
        { text: this.$t('subClaims.headers.updatedAt'), value: 'updatedAt' },
        { text: this.$t('subClaims.headers.status'), value: 'status' },
      ];
    },
  },
  data() {
    return {
      claim: JSON.parse(localStorage.getItem('claim')),
      deleteClicked: false,
      editClicked: false,
      deleteItem: null,
      loading: false,
      subClaims: [],
      search: null,
      searchField: 'all',
      createClicked: false,
      editCliked: false,
      editItem: {},
      subClaim: {
        title: '',
        description: '',
        claim: {},
      },
      duplicateNameExists: false,
      checkingDuplicate: false,
      createFormValid: false,
      editFormValid: false,
      rules: {
        required: value => !!value || 'Required.',
        duplicateName: () => !this.duplicateNameExists || 'SubClaim with this name already exists.',
      },
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = false;
    this.fetchSubClaims();
    this.$emit('loading', false);
    this.loading = false;
    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 500);
  },
  methods: {
    async fetchSubClaims() {
      try {
        const { data: rawSubClaims } = await subClaimsApi.all(this.claim.id);
        this.subClaims = rawSubClaims;
        // Fetch evidences count for each subclaim
        await this.fetchEvidencesCount();
      } catch (error) {
        if (error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      }
    },
    async fetchEvidencesCount() {
      try {
        for (let subClaim of this.subClaims) {
          try {
            // Get all evidences and filter by subclaim ID
            const { data: allEvidences } = await evidenceApi.all();
            const subClaimEvidences = allEvidences.filter(
              ev =>
                ev.subClaimsDto &&
                ev.subClaimsDto.some(sc => String(sc.id) === String(subClaim.id)),
            );
            this.$set(subClaim, 'evidencesCount', subClaimEvidences.length || 0);
          } catch (error) {
            this.$set(subClaim, 'evidencesCount', 0);
          }
        }
      } catch (error) {
        console.error('Error fetching evidences count:', error);
      }
    },
    async createSubClaim() {
      // Double-check for duplicates before proceeding
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'SubClaim with this name already exists',
          color: 'error',
        });
        return;
      }

      this.loading = true;
      this.subClaim.claim = JSON.parse(localStorage.getItem('claim'));
      try {
        await subClaimsApi.create(this.subClaim);
        EventBus.$emit('snackbar', {
          text: this.$t('create.successSubClaim'),
          color: 'green',
        });
        await this.fetchSubClaims();
      } catch (error) {
        console.error('Error occurred during creation:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.createClicked = false;
        this.resetSubClaim();
        this.loading = false;
      }
    },
    async checkDuplicateName() {
      if (!this.subClaim.title || this.subClaim.title.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }

      // Don't check if we're editing an existing subclaim with the same name
      if (this.editItem && this.editItem.id) {
        if (this.editItem.title === this.subClaim.title) {
          this.duplicateNameExists = false;
          return;
        }
      }

      this.checkingDuplicate = true;
      try {
        const response = await subClaimsApi.checkDuplicateName(this.subClaim.title);
        this.duplicateNameExists = response.data;

        // Force form validation to update
        this.$nextTick(() => {
          if (this.$refs.createForm) {
            this.$refs.createForm.validate();
          }
        });
      } catch (error) {
        console.error('Error checking duplicate name:', error);
        this.duplicateNameExists = false;
      } finally {
        this.checkingDuplicate = false;
      }
    },
    cancelCreate() {
      this.createClicked = false;
      this.resetSubClaim();
    },
    resetSubClaim() {
      this.subClaim.title = '';
      this.subClaim.description = '';
      this.subClaim.claim = {};
      this.duplicateNameExists = false;
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
    },
    handleSubClaimClicked(item) {
      localStorage.setItem('subClaim', JSON.stringify(item));
      this.editItem = item;
      // Save the original title to check for changes later
      this.originalTitle = item.title;
      this.editClicked = true;
    },
    enableDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteClicked = true;
    },
    async deleteClaim() {
      this.loading = true;
      try {
        await subClaimsApi.delete(this.deleteItem.id);
        // Find the index of the deleted item and remove it from the array
        const index = this.subClaims.findIndex(subClaim => subClaim.id === this.deleteItem.id);
        if (index !== -1) {
          this.subClaims.splice(index, 1);
        }
        EventBus.$emit('snackbar', {
          text: this.$t('delete.success'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error occurred during deletion:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.deleteClicked = false;
        this.loading = false;
      }
    },
    enableCreateDialog() {
      this.createClicked = true;
      this.duplicateNameExists = false;
      this.subClaim = {
        title: '',
        description: '',
        claim: {},
      };
      this.$nextTick(() => {
        if (this.$refs.createForm) {
          this.$refs.createForm.resetValidation();
        }
      });
    },
    async editClaim() {
      // Check for duplicates when renaming
      if (this.editItem.title !== this.originalTitle && this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'SubClaim with this name already exists',
          color: 'error',
        });
        return;
      }

      this.loading = true;
      try {
        await subClaimsApi.update(this.editItem);
        EventBus.$emit('snackbar', {
          text: this.$t('editSuccessSubClaim'),
          color: 'green',
        });
        await this.fetchSubClaims();
      } catch (error) {
        console.error('Error occurred during update:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.editClicked = false;
        this.loading = false;
      }
    },
    handleCancelEdit() {
      this.fetchSubClaims();
      this.editClicked = false;
    },
    statusChipColor(status) {
      switch (status) {
        case 'Draft':
          return 'orange';
        case 'Pending':
          return 'primary';
        case 'Completed':
          return 'green';
        default:
          return 'primary';
      }
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
    previewEvidence(item) {
      // Save the subclaim data to localStorage and navigate to Evidence Mapper
      localStorage.setItem('subClaim', JSON.stringify(item));
      this.$router.push({ name: 'SubClaimEvidenceMapper' });
    },
  },
};
</script>

<style scoped>
/* ── Breadcrumbs ── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}
.breadcrumbs-container .v-breadcrumbs {
  background: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ── Hero Card ── */
.sc-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 8px;
  border: none !important;
}
.sc-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 32px 36px;
  position: relative;
  overflow: hidden;
}
.sc-hero-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}
.sc-hero-header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}
.sc-hero-logo {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.sc-hero-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
}
.sc-hero-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

/* ── Workflow Section ── */
.sc-workflow-section {
  padding: 28px 36px 32px;
}

/* ── Metric Cards ── */
.sc-metric-card {
  background: #f8f9ff;
  border: 1px solid #e8eaf6;
  border-radius: 14px;
  padding: 22px;
  text-align: center;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
}
.sc-metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(26, 35, 126, 0.1);
}
.sc-metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}
.sc-metric-label {
  font-weight: 700;
  font-size: 1rem;
  color: #1a237e;
  margin-bottom: 6px;
}
.sc-metric-desc {
  font-size: 0.85rem;
  color: #546e7a;
  line-height: 1.5;
}

/* ── Content Wrapper ── */
.sc-content-wrapper {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}
/* ── Back Button ── */
.sc-back-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  padding: 8px 20px !important;
  text-transform: none !important;
  font-size: 0.95rem !important;
  border: 2px solid currentColor !important;
  transition: all 0.25s ease !important;
}
.sc-back-btn:hover {
  background: #e8eaf6 !important;
  transform: translateX(-3px);
}
/* ── Search Bar ── */
.sc-search-bar-container {
  border-radius: 12px !important;
  overflow: hidden;
  margin: 16px 0;
  border: none !important;
}
.sc-search-bar-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  padding: 10px 20px;
  display: flex;
  align-items: center;
}
.sc-search-bar-body {
  padding: 16px 20px 8px;
}

/* ── Table Card ── */
.sc-table-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: none !important;
}
.sc-table-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 12px 20px;
  display: flex;
  align-items: center;
}

/* ── Dialog Card ── */
.sc-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Search Field ── */
.search-field {
  min-width: 150px;
}
</style>
